import React, {useState, useEffect} from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    FormControlLabel,
    Switch,
    Box,
    Alert,
    CircularProgress,
    Typography,
    IconButton,
} from '@mui/material';
import {
    Close as CloseIcon,
    Edit as EditIcon,
} from '@mui/icons-material';

const UpdatePopup = ({
                         open,
                         onClose,
                         onUpdate,
                         entityType, // 'course' | 'topic'
                         entityData,
                         apiClient,
                         courseId,
                         topicId = null, // Only needed for topics
                     }) => {
    const [formData, setFormData] = useState({
        title: '',
        name: '',
        description: '',
        is_published: false,
        published: false,
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [validationErrors, setValidationErrors] = useState({});

    // Initialize form data when popup opens or entityData changes
    useEffect(() => {
        if (open && entityData) {
            setFormData({
                title: entityData.title || '',
                name: entityData.name || '',
                description: entityData.description || '',
                is_published: entityData.is_published || false,
                published: entityData.published || false,
            });
            setError(null);
            setValidationErrors({});
        }
    }, [open, entityData]);

    // Get configuration based on entity type
    const getConfig = () => {
        switch (entityType) {
            case 'course':
                return {
                    title: 'Edit Course',
                    primaryField: 'title',
                    primaryLabel: 'Course Title',
                    fields: ['title', 'description', 'published'],
                    endpoint: `/api/courses/${courseId}`,
                    submitData: {
                        title: formData.title,
                        description: formData.description,
                        published: formData.published,
                    },
                };
            case 'topic':
                return {
                    title: 'Edit Topic',
                    primaryField: 'name',
                    primaryLabel: 'Topic Name',
                    fields: ['name', 'description', 'is_published'],
                    endpoint: `/api/courses/${courseId}/topics/${topicId}`,
                    submitData: {
                        name: formData.name,
                        description: formData.description,
                        is_published: formData.is_published,
                    },
                };
            default:
                throw new Error(`Unsupported entity type: ${entityType}`);
        }
    };

    const config = getConfig();

    const handleInputChange = (field) => (event) => {
        const value = (field === 'is_published' || field === 'published') ? event.target.checked : event.target.value;
        setFormData(prev => ({
            ...prev,
            [field]: value,
        }));

        // Clear validation error for this field
        if (validationErrors[field]) {
            setValidationErrors(prev => ({
                ...prev,
                [field]: null,
            }));
        }
    };

    const validateForm = () => {
        const errors = {};
        const primaryFieldValue = formData[config.primaryField];

        if (!primaryFieldValue || primaryFieldValue.trim().length === 0) {
            errors[config.primaryField] = `${config.primaryLabel} is required`;
        } else if (primaryFieldValue.trim().length > 100) {
            errors[config.primaryField] = `${config.primaryLabel} must be 100 characters or less`;
        }

        // Validate description length if provided
        if (formData.description && formData.description.length > 1000) {
            errors.description = 'Description must be 1000 characters or less';
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await apiClient.put(config.endpoint, config.submitData);

            // Call the onUpdate callback with the updated data
            if (onUpdate) {
                onUpdate(response);
            }

            // Close the popup
            onClose();
        } catch (err) {
            console.error(`Error updating ${entityType}:`, err);

            // Handle different error types
            if (err.response?.status === 403) {
                setError(`You don't have permission to edit this ${entityType}.`);
            } else if (err.response?.status === 404) {
                setError(`${entityType} not found.`);
            } else if (err.response?.status === 400 && err.response.data?.detail) {
                setError(err.response.data.detail);
            } else {
                setError(`Failed to update ${entityType}. Please try again.`);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (!loading) {
            onClose();
        }
    };

    const hasChanges = () => {
        if (!entityData) return false;

        return config.fields.some(field => {
            const currentValue = formData[field];
            const originalValue = entityData[field];

            // Handle empty strings and null/undefined
            const normalizedCurrent = currentValue || '';
            const normalizedOriginal = originalValue || '';

            return normalizedCurrent !== normalizedOriginal;
        });
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {borderRadius: 2}
            }}
        >
            <DialogTitle sx={{pb: 1}}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box display="flex" alignItems="center">
                        <EditIcon sx={{mr: 1, color: 'primary.main'}}/>
                        <Typography variant="h6">{config.title}</Typography>
                    </Box>
                    <IconButton
                        aria-label="close"
                        onClick={handleClose}
                        disabled={loading}
                        size="small"
                    >
                        <CloseIcon/>
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent sx={{pt: 3, pb: 2}}>
                {error && (
                    <Alert severity="error" sx={{mb: 2}}>
                        {error}
                    </Alert>
                )}

                <Box sx={{display: 'flex', flexDirection: 'column', gap: 2.5, pt:3}}>
                    {/* Primary field (title for courses, name for topics) */}
                    <TextField
                        label={config.primaryLabel}
                        value={formData[config.primaryField]}
                        onChange={handleInputChange(config.primaryField)}
                        error={!!validationErrors[config.primaryField]}
                        helperText={validationErrors[config.primaryField]}
                        fullWidth
                        required
                        disabled={loading}
                        inputProps={{maxLength: 100}}
                    />

                    {/* Description field */}
                    <TextField
                        label="Description"
                        value={formData.description}
                        onChange={handleInputChange('description')}
                        error={!!validationErrors.description}
                        helperText={validationErrors.description}
                        fullWidth
                        multiline
                        rows={3}
                        disabled={loading}
                        inputProps={{maxLength: 1000}}
                    />

                    {/* Published toggle for topics */}
                    {entityType === 'topic' && (
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.is_published}
                                    onChange={handleInputChange('is_published')}
                                    disabled={loading}
                                />
                            }
                            label="Published"
                            sx={{mt: 1}}
                        />
                    )}

                    {/* Published toggle for courses */}
                    {entityType === 'course' && (
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.published}
                                    onChange={handleInputChange('published')}
                                    disabled={loading}
                                />
                            }
                            label="Published"
                            sx={{mt: 1}}
                        />
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={{px: 3, pb: 2}}>
                <Button
                    onClick={handleClose}
                    disabled={loading}
                    color="inherit"
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={loading || !hasChanges()}
                    startIcon={loading ? <CircularProgress size={16}/> : null}
                >
                    {loading ? 'Updating...' : `Update ${entityType === 'course' ? 'Course' : 'Topic'}`}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default UpdatePopup;